import { Monitor, Smartphone } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FaApple, FaWindows } from "react-icons/fa";
import { IoLogoAndroid } from "react-icons/io";
import { IoMdTabletLandscape } from "react-icons/io";
import type { IconType } from "react-icons";

export type BugReportDeviceType = "DESKTOP" | "MOBILE" | "TABLET";
export type BugReportOs = "ANDROID" | "IOS" | "WINDOWS";

/** Egna PNG-ikoner för enhet (t.ex. i public/icons/) – tom = använd Lucide/Ionicons. */
export const CUSTOM_DEVICE_ICON_URLS: Partial<
  Record<BugReportDeviceType, string>
> = {};

const DEVICE_ICONS: Partial<Record<BugReportDeviceType, LucideIcon>> = {
  DESKTOP: Monitor,
  MOBILE: Smartphone,
  /* TABLET: Ionicons IoTabletPortrait, renderas separat nedan */
};

const DEVICE_LABELS: Record<BugReportDeviceType, string> = {
  DESKTOP: "Desktop / Laptop",
  MOBILE: "Mobil",
  TABLET: "Surfplatta",
};

const OS_LABELS: Record<BugReportOs, string> = {
  ANDROID: "Android",
  IOS: "iOS",
  WINDOWS: "Windows",
};

const OS_ICONS: Record<BugReportOs, IconType> = {
  ANDROID: IoLogoAndroid,
  IOS: FaApple,
  WINDOWS: FaWindows,
};

interface IconProps {
  value: BugReportDeviceType | BugReportOs;
  className?: string;
  title?: string;
}

function CustomIconImg({
  src,
  alt,
  className,
  title,
}: {
  src: string;
  alt: string;
  className?: string;
  title?: string;
}) {
  return (
    <img
      src={src}
      alt={alt}
      title={title}
      className={`shrink-0 object-contain ${className ?? "h-4 w-4"}`}
      aria-hidden
    />
  );
}

export function DeviceIcon({ value, className = "h-4 w-4", title }: IconProps) {
  const deviceValue = value as BugReportDeviceType;
  const label = DEVICE_LABELS[deviceValue];
  const customUrl = CUSTOM_DEVICE_ICON_URLS[deviceValue];
  if (customUrl) {
    return (
      <CustomIconImg
        src={customUrl}
        alt={label}
        className={className}
        title={title ?? label}
      />
    );
  }
  if (deviceValue === "TABLET") {
    return (
      <IoMdTabletLandscape
        className={className}
        title={title ?? label}
        aria-hidden
      />
    );
  }
  const Icon = DEVICE_ICONS[deviceValue];
  if (!Icon) return null;
  return (
    <span title={title ?? label}>
      <Icon className={className} aria-hidden />
    </span>
  );
}

/** Operativsystem-ikoner från react-icons (FaApple, IoLogoAndroid, FaWindows). */
export function OsIcon({ value, className = "h-4 w-4", title }: IconProps) {
  const osValue = value as BugReportOs;
  const Icon = OS_ICONS[osValue];
  const label = OS_LABELS[osValue];
  if (!Icon) return null;
  return <Icon className={className} title={title ?? label} aria-hidden />;
}

export function DeviceIconWithLabel({
  value,
  className,
  iconClassName = "h-4 w-4",
  labels,
}: {
  value: BugReportDeviceType;
  className?: string;
  iconClassName?: string;
  labels?: Partial<Record<BugReportDeviceType, string>>;
}) {
  const label = labels?.[value] ?? DEVICE_LABELS[value];
  return (
    <span className={`inline-flex items-center gap-1.5 ${className ?? ""}`}>
      <DeviceIcon value={value} className={iconClassName} />
      <span>{label}</span>
    </span>
  );
}

export function OsIconWithLabel({
  value,
  className,
  iconClassName = "h-4 w-4",
  labels: labelsProp,
}: {
  value: BugReportOs;
  className?: string;
  iconClassName?: string;
  labels?: Partial<Record<BugReportOs, string>>;
}) {
  const label = labelsProp?.[value] ?? OS_LABELS[value];
  return (
    <span className={`inline-flex items-center gap-1.5 ${className ?? ""}`}>
      <OsIcon value={value} className={iconClassName} />
      <span>{label}</span>
    </span>
  );
}

export { DEVICE_ICONS, DEVICE_LABELS, OS_LABELS, OS_ICONS };
